import { put } from 'redux-saga/effects';
import errorHandler from 'utils/apiHandler';
import { toast } from 'react-toastify';
import {
  getPropertyListStart,
  getPropertyListSuccess,
  getPropertyListFail,
  createPropertyStat,
  createPropertySuccess,
  createPropertyFail,
  mintPropertyStart,
  mintPropertySuccess,
  mintPropertyFail,
  mintProperty,
  deletePropertyStart,
  deletePropertyFail,
  deletePropertySuccess,
  togglePropertyHideStart,
  togglePropertyHideSuccess,
  togglePropertyHideFail,
  updatePropertyStatusStart,
  updatePropertyStatusSuccess,
  updatePropertyStatusFail,
  deletePropertyAdditionalDocumentStart,
  deletePropertyAdditionalDocumentSuccess,
  deletePropertyAdditionalDocumentFail,
  addPropertyAdditionalDocumentStart,
  addPropertyAdditionalDocumentSuccess,
  addPropertyAdditionalDocumentFail,
} from 'store/actions';
import { marketMain } from 'http/axios/axios_main';

export function* fetchPropertyList({ payload }) {
  yield put(getPropertyListStart());
  yield errorHandler({
    endpoint: `/property?${payload || ''}`,
    successHandler: yield function* (response) {
      const { data } = response;
      yield put(getPropertyListSuccess(data));
    },
    failHandler: yield function* (response) {
      yield toast.error(response);
      yield put(getPropertyListFail(response));
    },
    failHandlerType: 'CUSTOM',
    apiType: 'get',
    token: true,
  });
}

export function* addProperty(action) {
  yield put(createPropertyStat());
  const { _id, data, success, event, status, onFail } = action.payload;

  delete data.venly;
  delete data.blockchainAddress;

  yield errorHandler({
    endpoint: _id
      ? status === 'Draft'
        ? `/property/${_id}`
        : `/property/${_id}/updateMintedProperty`
      : '/property',
    successHandler: yield function* (response) {
      if (event === 'mint') {
        yield put(mintProperty({ id: _id, success }));
        return;
      }
      yield put(createPropertySuccess(response.data));
      if (success) yield success(response.data);
    },
    failHandler: yield function* (response) {
      if (onFail) yield onFail();
      yield toast.error(response);
      yield put(createPropertyFail(response));
    },
    payload: data,
    failHandlerType: 'CUSTOM',
    apiType: _id ? 'patch' : 'post',
    token: true,
  });
}

export function* mintPropertySaga({ payload }) {
  yield put(mintPropertyStart());
  yield errorHandler({
    endpoint: `/property/${payload.id}/mint`,
    successHandler: yield function* (response) {
      const { data } = response;
      yield put(mintPropertySuccess(data));
      yield payload.success();
    },
    failHandler: yield function* (response) {
      yield toast.error(response);
      yield put(mintPropertyFail(response));
    },
    failHandlerType: 'CUSTOM',
    apiType: 'post',
    token: true,
  });
}

export function* putOnSaleProperty({ payload }) {
  yield put(mintPropertyStart());
  yield errorHandler({
    endpoint: `/marketplace/put-on-sale/${payload.id}`,
    successHandler: yield function* (response) {
      const { data } = response;
      yield put(mintPropertySuccess(data));
      yield payload.success();
    },
    failHandler: yield function* (response) {
      yield toast.error(response);
      yield put(mintPropertyFail(response));
    },
    baseAxios: marketMain,
    failHandlerType: 'CUSTOM',
    apiType: 'post',
    token: true,
  });
}

export function* buyMogulEquity({ payload }) {
  yield put(mintPropertyStart());
  yield errorHandler({
    endpoint: '/marketplace/invest',
    successHandler: yield function* (response) {
      const { data } = response;
      yield put(mintPropertySuccess(data));
      yield payload.success();
    },
    failHandler: yield function* (response) {
      yield toast.error(response);
      yield put(mintPropertyFail(response));
    },
    baseAxios: marketMain,
    failHandlerType: 'CUSTOM',
    apiType: 'post',
    token: true,
    payload: payload.data,
  });
}

export function* deleteProperty(action) {
  yield put(deletePropertyStart());
  const { _id } = action.payload;
  yield errorHandler({
    endpoint: `/property/${_id}`,
    successHandler: yield function* () {
      yield put(deletePropertySuccess());
    },
    failHandler: yield function* (response) {
      yield toast.error(response);
      yield put(deletePropertyFail(response));
    },
    failHandlerType: 'CUSTOM',
    apiType: 'delete',
    token: true,
  });
}

export function* togglePropertyHideSaga(action) {
  yield put(togglePropertyHideStart());
  const { propertyId, isHidden, onSuccess, onFail } = action.payload;
  
  yield errorHandler({
    endpoint: `/admin/properties/${propertyId}/hide`,
    successHandler: yield function* (response) {
      yield put(togglePropertyHideSuccess(response.data));
      if (onSuccess) yield onSuccess();
      yield toast.success(`Property ${isHidden ? 'hidden' : 'shown'} successfully!`);
    },
    failHandler: yield function* (response) {
      yield toast.error(response);
      yield put(togglePropertyHideFail(response));
      if (onFail) yield onFail();
    },
    payload: { isHidden },
    failHandlerType: 'CUSTOM',
    apiType: 'put',
    token: true,
  });
}

export function* updatePropertyStatusSaga(action) {
  yield put(updatePropertyStatusStart());
  const { propertyId, status, propertyName, onSuccess, onFail } = action.payload;
  
  yield errorHandler({
    endpoint: `/admin/properties/${propertyId}/status`,
    successHandler: yield function* (response) {
      yield put(updatePropertyStatusSuccess(response.data));
      yield toast.success(`Property "${propertyName || propertyId}" status updated to "${status}" successfully!`);
      if (onSuccess) yield onSuccess();
    },
    failHandler: yield function* (response) {
      yield toast.error(response || 'Failed to update property status');
      yield put(updatePropertyStatusFail(response));
      if (onFail) yield onFail();
    },
    payload: { status },
    failHandlerType: 'CUSTOM',
    apiType: 'put',
    token: true,
  });
}

export function* deletePropertyAdditionalDocumentSaga(action) {
  yield put(deletePropertyAdditionalDocumentStart());
  const { propertyId, documentId, onSuccess, onFail } = action.payload;
  
  yield errorHandler({
    endpoint: `/admin/properties/${propertyId}/additional-documents/${documentId}`,
    successHandler: yield function* (response) {
      yield put(deletePropertyAdditionalDocumentSuccess(response.data));
      yield toast.success('Document deleted successfully!');
      if (onSuccess) yield onSuccess();
    },
    failHandler: yield function* (response) {
      yield toast.error(response || 'Failed to delete document');
      yield put(deletePropertyAdditionalDocumentFail(response));
      if (onFail) yield onFail();
    },
    failHandlerType: 'CUSTOM',
    apiType: 'delete',
    token: true,
  });
}

export function* addPropertyAdditionalDocumentSaga(action) {
  yield put(addPropertyAdditionalDocumentStart());
  const { propertyId, files, onSuccess, onFail } = action.payload;
  
  // Create FormData
  const formData = new FormData();
  files.forEach(file => {
    formData.append('additionalDocumentsByAdmin', file);
  });
  
  yield errorHandler({
    endpoint: `/admin/properties/${propertyId}/additional-documents`,
    successHandler: yield function* (response) {
      yield put(addPropertyAdditionalDocumentSuccess(response.data));
      yield toast.success('Documents uploaded successfully!');
      if (onSuccess) yield onSuccess();
    },
    failHandler: yield function* (response) {
      const errorMsg =
        response?.includes('413') || response?.includes('too large')
          ? 'File size too large to upload'
          : response || 'Failed to upload documents. Please try again.';
      yield toast.error(errorMsg);
      yield put(addPropertyAdditionalDocumentFail(response));
      if (onFail) yield onFail();
    },
    payload: formData,
    failHandlerType: 'CUSTOM',
    apiType: 'post',
    token: true,
  });
}